import React, { FC, useState } from 'react';
import { Box, Button, Chip, Menu, MenuItem, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useMutation } from '@apollo/client';
import { ADD_PROJECT_LEAD_ASSESSMENT, SET_PROGRESS_RESPONSIBILITY } from './revisions.graphql';
import { Revision } from './Details';
import { StyledPaper } from '../theme';
import { RevisionRoles } from '../__generated__/graphql';
import { progressResponsibilityRoles, revisionRoleToDisplayText, sortProgressResponsibility } from './RevisionModel';

export const ProgressResponsibility: FC<{ revision: Revision; readonly: boolean }> = ({ revision, readonly }) => {
  const [setProgressResponsibility] = useMutation(SET_PROGRESS_RESPONSIBILITY);
  const [addAssessment, { loading: saving }] = useMutation(ADD_PROJECT_LEAD_ASSESSMENT);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [comment, setComment] = useState<string | null>(null);

  const current = sortProgressResponsibility(revision?.progressResponsibility);
  const available = progressResponsibilityRoles.filter((role) => !current.includes(role));

  const update = async (roles: RevisionRoles[]) => {
    await setProgressResponsibility({ variables: { input: { id: revision?.id, roles } } });
    setComment('');
  };

  const saveComment = async () => {
    const text = comment?.trim();
    setComment(null);
    if (!text) return;

    await addAssessment({ variables: { input: { id: revision?.id, text } } });
  };

  const handleAdd = (role: RevisionRoles) => {
    setMenuAnchor(null);
    update([...current, role]);
  };

  return (
    <StyledPaper>
      <Typography variant="h5" component="h3" gutterBottom>
        Ligger hos
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        {current.map((role) => (
          <Chip
            key={role}
            label={revisionRoleToDisplayText(role)}
            color="primary"
            onDelete={readonly ? undefined : () => update(current.filter((x) => x !== role))}
          />
        ))}
        {current.length === 0 && readonly && <Typography color="textSecondary">Ikke satt</Typography>}
        {!readonly && available.length > 0 && (
          <>
            <Chip icon={<AddIcon />} label="Legg til" variant="outlined" onClick={(event) => setMenuAnchor(event.currentTarget)} />
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              {available.map((role) => (
                <MenuItem key={role} onClick={() => handleAdd(role)}>
                  {revisionRoleToDisplayText(role)}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Stack>
      {comment !== null && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="standard"
            placeholder="Hvorfor ble den flyttet? (valgfritt)"
            helperText="Lagres som en prosjektoppdatering."
            value={comment}
            disabled={saving}
            onChange={(event) => setComment(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                saveComment();
              }
              if (event.key === 'Escape') setComment(null);
            }}
          />
          <Button size="small" onClick={saveComment} disabled={saving || !comment.trim()} sx={{ mb: 2.5 }}>
            Lagre
          </Button>
          <Button size="small" color="inherit" onClick={() => setComment(null)} disabled={saving} sx={{ mb: 2.5 }}>
            Avbryt
          </Button>
        </Box>
      )}
    </StyledPaper>
  );
};
